import React, { useState } from "react";
import { Plus, Users, Building, FileText } from "lucide-react";
import { AddClientModal } from "./AddClientModal";
import { AddCompanyModal } from "./AddCompanyModal";
import { AddFormaliteModal } from "./AddFormaliteModal";
import { Customer, Company, Formalite, baserowAPI, TABLES } from "../services/baserowAPI";

interface DataManagementSectionProps {
  onDataUpdated?: () => void;
}

export const DataManagementSection: React.FC<DataManagementSectionProps> = ({
  onDataUpdated,
}) => {
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isFormaliteModalOpen, setIsFormaliteModalOpen] = useState(false);

  const handleClientCreated = (client: Customer) => {
    console.log("Nouveau client créé:", client);
    if (onDataUpdated) {
      onDataUpdated();
    }
  };

  const handleCompanyCreated = (company: Company) => {
    console.log("Nouvelle entreprise créée:", company);
    if (onDataUpdated) {
      onDataUpdated();
    }
  };

  const handleFormaliteCreated = async (formalite: Partial<Formalite>) => {
    try {
      console.log("Création de la formalité avec les données:", formalite);
      const newFormalite = await baserowAPI.createRow<Formalite>(TABLES.FORMALITIES, formalite);
      console.log("Nouvelle formalité créée:", newFormalite);
      if (onDataUpdated) {
        onDataUpdated();
      }
    } catch (error) {
      console.error("Erreur lors de la création de la formalité:", error);
      throw error;
    }
  };

  return (
    <>
      {/* Section de gestion des données */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-blue-600" />
            Gestion des données
          </h2>
          <p className="text-sm text-gray-600">
            Créer de nouveaux clients, entreprises et formalités
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Bouton Nouveau Client */}
          <button
            onClick={() => setIsClientModalOpen(true)}
            className="flex items-center justify-center p-6 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group"
          >
            <div className="text-center">
              <Users className="w-8 h-8 text-blue-500 mx-auto mb-2 group-hover:text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Nouveau Client
              </h3>
              <p className="text-sm text-gray-600">
                Ajouter un client avec ses informations de contact
              </p>
            </div>
          </button>

          {/* Bouton Nouvelle Entreprise */}
          <button
            onClick={() => setIsCompanyModalOpen(true)}
            className="flex items-center justify-center p-6 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors group"
          >
            <div className="text-center">
              <Building className="w-8 h-8 text-green-500 mx-auto mb-2 group-hover:text-green-600" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Nouvelle Entreprise
              </h3>
              <p className="text-sm text-gray-600">
                Ajouter une entreprise avec son SIRET
              </p>
            </div>
          </button>

          {/* Bouton Nouvelle Formalité */}
          <button
            onClick={() => setIsFormaliteModalOpen(true)}
            className="flex items-center justify-center p-6 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors group"
          >
            <div className="text-center">
              <FileText className="w-8 h-8 text-purple-500 mx-auto mb-2 group-hover:text-purple-600" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Nouvelle Formalité
              </h3>
              <p className="text-sm text-gray-600">
                Ajouter une formalité avec ses échéances
              </p>
            </div>
          </button>
        </div>

        {/* Informations supplémentaires */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            💡 Informations utiles
          </h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>
              • Les <strong>clients</strong> sont les personnes ou entités qui
              vous confient des formalités
            </li>
            <li>
              • Les <strong>entreprises</strong> sont les structures juridiques
              pour lesquelles les formalités sont effectuées
            </li>
            <li>
              • Les <strong>formalités</strong> sont les démarches
              administratives à effectuer avec leurs échéances
            </li>
            <li>
              • Un même client peut avoir plusieurs entreprises et plusieurs
              formalités
            </li>
            <li>
              • Ces données seront disponibles lors de la création de nouvelles
              entrées
            </li>
          </ul>
        </div>
      </div>

      {/* Modales */}
      <AddClientModal
        isOpen={isClientModalOpen}
        onClose={() => setIsClientModalOpen(false)}
        onClientCreated={handleClientCreated}
      />

      <AddCompanyModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onCompanyCreated={handleCompanyCreated}
      />

      <AddFormaliteModal
        isOpen={isFormaliteModalOpen}
        onClose={() => setIsFormaliteModalOpen(false)}
        onSave={handleFormaliteCreated}
      />
    </>
  );
};
